import { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { useAppDispatch, useAppSelector } from 'store/hooks.ts';
import { selectAuthTokenVerification } from 'store/authToken/authToken.selector.ts';
import {
  setupAuthorizationDatabaseAndSaveToken,
  verifyToken,
} from 'store/authToken/authToken.thunk.ts';
import {
  completeSession,
  isSessionIncomplete,
  syncSessionExpiration,
} from 'store/authToken/authToken.session.ts';
import { Loading, ToastMessage } from 'components/primitive';
import { GetSitePermission } from '@/utilities';
import SitePermissionTypes from '@/common/enum/SitePermissionTypes.ts';
import { SiteStatusCodes, ToastStatus } from '@/common/enum';
import { UserAuthCreds } from 'interfaces/UserAccountInfo.interface.ts';
import { setAuthCreds, setJwtToken } from 'store/authToken/authToken.action.ts';
import { setAccount, setLoadingState } from 'store/accountsInfo/accountsInfo.actions.ts';
import { selectAppLoading } from 'store/accountsInfo/accountsInfo.selector.ts';
import { selectConnectionHealth } from 'store/connection/connection.selector.ts';
import useStartHealthChecker from '@/utilities/startHealthChecker.ts';
import { Outlet, useNavigate, useSearchParams } from 'react-router-dom';

// Site status codes that resolve to a matching accessType -- ACTIVE with a
// recognized permission letter, or ACTIVE with none at all (which defaults to
// Grant) -- never reach this map. Everything else -- pending, suspended, or an
// unrecognized/denied status -- gets a message a user can act on instead of
// the same generic "no access" line for every case.
const NO_ACCESS_MESSAGES: Record<string, string> = {
  [SiteStatusCodes.REQUEST]: 'Your access request for this application is still pending approval.',
  [SiteStatusCodes.SUSPEND]:
    'Your access to this application has been suspended. Contact an administrator.',
};

// Shown to a user whose session was established before this build and cannot be
// renewed. Their saved work is untouched -- it lives in the assessment database,
// which signing in again does not clear -- so the message says so rather than
// leaving them to guess.
const REAUTH_REQUIRED_MESSAGE =
  'Please sign in again to continue. Your saved assessments are safe.';

const AuthPage = () => {
  const [searchParams] = useSearchParams();
  const [idTokenProcessed, setIdTokenProcessed] = useState(false);
  const idToken = searchParams.get('id_token');

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isAppLoading = useAppSelector(selectAppLoading);
  const authTokenVerification = useAppSelector(selectAuthTokenVerification);
  const health = useAppSelector(selectConnectionHealth);
  // Latches the restored-session check to once per app load. See the effect below.
  const reauthCheckedRef = useRef(false);
  // True once a session has actually been established in this app load. It is
  // what separates "this sign-in attempt failed" from "a working session was
  // ended underneath us", which the verification effect below cannot otherwise
  // tell apart -- both arrive as a falsy `authTokenVerification`.
  const hadSessionRef = useRef(false);

  useStartHealthChecker();

  // A session persisted by a build that did not carry the ID token rehydrates
  // looking signed in while holding no credential the Damage Assessment API
  // accepts. Renew it where a Cognito refresh token exists; where none does --
  // the portal hand-off never had one -- say so and send the user to sign in
  // again.
  //
  // Three states hold this back rather than firing it wrongly:
  //   `?id_token=` present -- a sign-in is already in flight and will complete
  //                           the session itself.
  //   offline              -- an offline device can neither renew a session nor
  //                           sign in again, so the prompt would strand a field
  //                           user and their unsynced queue. Wait for the
  //                           connection; `health` flipping true re-runs this.
  //   already checked      -- once per app load. The latch is a ref, never the
  //                           dependency, so this effect cannot retrigger itself.
  useEffect(() => {
    if (idToken?.length || !health || reauthCheckedRef.current) return;
    if (!isSessionIncomplete()) return;
    let abandoned = false;
    completeSession()
      .then((complete) => {
        // Re-read the store rather than trusting the value this started with: a
        // sign-in may have landed while the refresh was in flight.
        if (abandoned || complete || !isSessionIncomplete()) return;
        toast.info(<ToastMessage status={ToastStatus.ERROR} message={REAUTH_REQUIRED_MESSAGE} />);
        // SPA navigation, not a page reload: a reload would unmount the toast
        // before the user could read the one message this path exists to deliver.
        navigate('/login');
      })
      .catch((error) => console.warn('Could not complete the restored session', error))
      .finally(() => {
        // Latch only once the check actually finished -- setting it before the
        // await let a health flap's cleanup (abandoned = true) permanently block
        // retry on an attempt that never got to run.
        if (!abandoned) reauthCheckedRef.current = true;
      });
    return () => {
      abandoned = true;
    };
    // idToken is read above but omitted as a dependency: it flips from a value to
    // null when the third effect's navigate('/loading') clears the query param,
    // and re-running this effect on that transition would re-check a session this
    // effect had no part in establishing. navigate is a stable reference from
    // useNavigate() and safe to omit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [health]);

  useEffect(() => {
    if (idToken?.length) {
      dispatch(verifyToken(idToken)).then(() => {
        setIdTokenProcessed(true);
      });
    } else {
      setIdTokenProcessed(true);
    }
  }, [idToken, dispatch]);

  useEffect(() => {
    if (!idTokenProcessed) return;
    try {
      if (
        authTokenVerification &&
        authTokenVerification.attributes &&
        authTokenVerification.accessToken
      ) {
        hadSessionRef.current = true;
        let accessType;
        const groupId = authTokenVerification.pvActiveGroupID;
        const sitePermission = GetSitePermission('DMAM', groupId, authTokenVerification.attributes);
        const permissions = sitePermission.sitePermissions;

        switch (permissions && permissions[0]) {
          case 'A':
            accessType = SitePermissionTypes.A;
            break;
          case 'F':
            accessType = SitePermissionTypes.F;
            break;
          case 'U':
            accessType = SitePermissionTypes.U;
            break;
          case 'L':
            accessType = SitePermissionTypes.L;
            break;
          case 'G':
            accessType = SitePermissionTypes.G;
            break;
          default:
            // Active-but-unpermissioned DMAM records default to Grant (the
            // least-privileged tier) rather than blocking sign-in entirely.
            accessType =
              sitePermission.siteStatus === SiteStatusCodes.ACTIVE ? SitePermissionTypes.G : null;
        }

        if (!accessType) {
          throw new Error(
            NO_ACCESS_MESSAGES[sitePermission.siteStatus] ??
              "You don't have access to the Damage Assessment Mobile App."
          );
        }

        const userAuthCreds: UserAuthCreds = {
          id: authTokenVerification.accessToken,
          userId: authTokenVerification.id,
          username: authTokenVerification.username,
          accessType: accessType,
        };
        sessionStorage.setItem('userAuthCreds', JSON.stringify(userAuthCreds));
        // The offline auth database is written asynchronously, so this synchronous
        // try/catch never saw its failure: the `return` it guarded was unreachable and
        // a rejected write escaped as an unhandled rejection. Sign-in still proceeds --
        // the session itself lives in Redux and sessionStorage -- but the failure is
        // reported now instead of vanishing.
        setupAuthorizationDatabaseAndSaveToken(userAuthCreds).catch((error) =>
          console.error('Could not write the offline authorization database', error)
        );
        // The portal hand-off is the only path whose ID token arrives in the URL
        // rather than in a Cognito session, so it is stored here. It is stored
        // inside this branch deliberately: a token that failed verification
        // establishes no session, and must leave nothing behind. The
        // username/password path already stored its own in cognitoLogin.fulfilled.
        //
        // Stored before the opaque token, so the session is never momentarily
        // readable as "signed in, no ID token" -- the shape the restored-session
        // check above treats as a session needing renewal.
        if (idToken?.length) {
          dispatch(setJwtToken(idToken));
        }
        dispatch(setAuthCreds(userAuthCreds));
        // Record when this session dies. Both entry paths land here: the Cognito
        // sign-in (expiry read from the pool's stored session) and the portal
        // hand-off (expiry read from the ?id_token= JWT, which cannot be renewed).
        syncSessionExpiration(idToken).catch((error) =>
          console.warn('Could not record session expiration', error)
        );
        dispatch(setAccount(authTokenVerification));
        dispatch(setLoadingState(false));
        return navigate('/loading');
      } else if (hadSessionRef.current) {
        // A session existed and has now been cleared -- `endSession()` ran
        // because the token expired or its identity claims stopped resolving.
        // That is not a failed sign-in, and whoever called `endSession()`
        // already told the user why, so this routes to re-auth without adding
        // a second, contradictory message on top of theirs.
        //
        // This branch is also the only route to `/login` for every surface
        // that is not the Queue -- the Map and the IA/PA detail pages reach
        // `apiFetch` through thunks that have no navigation of their own. It
        // previously happened by accident, via the throw below; losing it
        // would strand those users on a cleared store with no way back in.
        hadSessionRef.current = false;
        dispatch(setLoadingState(false));
        return navigate('/login');
      } else {
        throw new Error('Authentication failed. Please try again.');
      }
    } catch (error) {
      console.log('Error occured', error);
      dispatch(setLoadingState(false));
      toast.info(
        <ToastMessage
          status={ToastStatus.ERROR}
          message={
            error instanceof Error ? error.message : 'Authentication failed. Please try again.'
          }
        />
      );
      return navigate('/login');
    }
    // dispatch is omitted deliberately (stable, adding it is a no-op); idToken and
    // navigate are omitted because they're not safe to add. AuthPage is a layout
    // route that stays mounted across navigation (see the authpage-forces-loading-
    // route-on-every-mount learning), so the navigate('/loading') call above clears
    // the id_token query param, which flips idToken to null on the next render --
    // if idToken were a dependency, that change (caused by this effect's own
    // action) would re-fire it and duplicate the offline-auth-db write and the
    // dispatches above. Same effect-triggers-itself shape as the bug fixed in
    // LoadingContainer.tsx (loading-flag-as-guard-and-dependency).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authTokenVerification, idTokenProcessed]);

  if (isAppLoading) {
    return <Loading message={'Authentication in process, please wait...'} />;
  } else {
    return <Outlet />;
  }
};

export default AuthPage;
